import type { Observable } from 'rxjs'
import { EMPTY, from, of, catchError } from 'rxjs'

/**
 * Copies the value to the device's clipboard
 * @param {string} value
 * @param {string} [html] Editor HTML for rich-text paste.
 * @returns {Observable<void>} Emits after a verified copy and completes silently when copying fails.
 */
export function copyToClipboard(value: string, html?: string): Observable<void> {
  if (html && navigator?.clipboard?.write && typeof ClipboardItem !== 'undefined') {
    return from(
      navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([value], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' })
        })
      ])
    ).pipe(catchError(() => copyToClipboard(value)))
  }

  if (navigator?.clipboard?.writeText) {
    return from(navigator.clipboard.writeText(value)).pipe(
      catchError(() => {
        return copyToClipboardForUnsupportedBrowser(value) ? of(undefined) : EMPTY
      })
    )
  }

  return copyToClipboardForUnsupportedBrowser(value) ? of(undefined) : EMPTY
}

/**
 * Fallback function to copy to clipboard in legacy browsers
 * @param {string} value
 * @returns {boolean} Whether the browser accepted the copy command.
 */
function copyToClipboardForUnsupportedBrowser(value: string): boolean {
  const selBox = document.createElement('textarea')

  selBox.style.position = 'fixed'
  selBox.style.left = '0'
  selBox.style.top = '0'
  selBox.style.opacity = '0'
  selBox.value = value

  document.body.appendChild(selBox)
  selBox.focus()
  selBox.select()

  try {
    return document.execCommand('copy')
  } finally {
    document.body.removeChild(selBox)
  }
}
